import { Box } from "@mui/material";
import React, { useRef, useState, useEffect, JSX } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

type DataItem = {
  name: string;
  value: number;
  color: string;
};

const Donut: React.FC<{ data: DataItem[] }> = ({ data }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [legends, setLegends] = useState<JSX.Element[]>([]);
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const baseRadius = 40;
  const [width, setWidth] = useState(0);

  // Fixed label position calculation - uses Recharts angle convention
  // (0 degrees at 12 o'clock, going clockwise)
  const calculateLabelPosition = (
    startAngle: number,
    endAngle: number,
    radius: number
  ) => {
    // Convert to radians and adjust for Recharts angle system
    const angleInRadians = ((startAngle + endAngle) / 2 - 90) * (Math.PI / 180);

    // Base positioning
    const x = Math.cos(angleInRadians) * radius + (ref.current?.clientWidth || 0) / 2;
    const y = Math.sin(angleInRadians) * radius + (ref.current?.clientHeight || 0) / 2;

    // Apply additional vertical offset based on position
    // Move top label higher and bottom label lower
    const verticalOffset = Math.sin(angleInRadians) < 0 ? -60 : 60;
    const horizontalOffset = Math.cos(angleInRadians) < 0 ? 40 : -40;

    return {
      x: x + horizontalOffset,
      y: y + verticalOffset
    };
  };

  useEffect(() => {
    const handleResize = () => {
      setWidth(ref.current?.clientWidth!);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [ref.current]);

  useEffect(() => {
    let cumulativeAngle = 0;
    const newLegends = data.map((entry) => {
      const valuePercent = (entry.value / totalValue) * 100;
      const angleDelta = (valuePercent / 100) * 360;
      const startAngle = cumulativeAngle;
      const endAngle = cumulativeAngle + angleDelta;
      cumulativeAngle += angleDelta;

      const labelPos = calculateLabelPosition(startAngle, endAngle, 130);

      return (
        <text
          key={entry.name}
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: 12, fontFamily: "Poppins" }}
        >
          {`${entry.name} (${valuePercent.toFixed(1)}%)`}
        </text>
      );
    });
    setLegends(newLegends);
  }, [data, totalValue]);

  return (
    <Box ref={ref} width="100%" height={300}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <g>{legends}</g>
          {data.map((entry, index) => {
            const valuePercent = (entry.value / totalValue) * 100;
            const angleDelta = (valuePercent / 100) * 360;
            const startAngle = data
              .slice(0, index)
              .reduce((sum, item) => sum + (item.value / totalValue) * 360, 0);
            const endAngle = startAngle + angleDelta;

            // Use a consistent outer radius for a cleaner donut appearance
            // or adjust if you want segments of different thickness
            const radiusScale = entry.value / totalValue;
            const outerRadius = baseRadius + radiusScale * 30 + 5;

            return (
              <Pie
                key={`pie-${index}`}
                data={[entry]}
                cx="50%"
                cy="50%"
                innerRadius={baseRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                dataKey="value"
              >
                <Cell strokeWidth={0.0} fill={entry.color} />
              </Pie>
            );
          })}
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default Donut;